import { useCallback, useEffect, useState } from 'react';
import Icon from '../components/Icon';
import Modal from '../components/Modal';
import { ErrorBanner, InfoBanner, Toast } from '../components/Feedback';
import { api, currentDeviceId } from '../api/client';
import { useAuth } from '../context/AuthContext';

/**
 * Module 10's Device Management screen.
 *
 * <p>The cap is the reason this screen exists, so it leads with the count rather than
 * burying it: someone arrives here because a sign-in somewhere else was refused, and
 * "2 of 2 devices" answers that in a glance where a list of rows does not.
 */
export default function DevicesPage() {
  const { user, logout } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [signingOut, setSigningOut] = useState(null);
  const [toast, setToast] = useState(null);

  const thisDevice = currentDeviceId();

  const load = useCallback(() => {
    setLoading(true);
    api
      .devices()
      .then(setData)
      .catch(setError)
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  async function signOut(device) {
    setSigningOut(null);
    setError(null);
    // Signing out the device you are on ends this session too, so go through the same
    // logout the header uses rather than leaving the page holding a dead token.
    if (device.deviceFingerprint === thisDevice) {
      logout();
      return;
    }
    try {
      setData(await api.signOutDevice(device.id));
      setToast(`${device.deviceLabel} signed out`);
    } catch (err) {
      setError(err);
    }
  }

  const devices = data?.devices ?? [];
  const capped = (data?.limit ?? 0) > 0;
  const full = capped && data.inUse >= data.limit;

  return (
    <>
      <div className="page-head row-between">
        <div>
          <h1>My Devices</h1>
          <p>
            {capped
              ? `You can be signed in on ${data.limit} device${data.limit === 1 ? '' : 's'} at a time.`
              : 'Everywhere your account currently has a session.'}
          </p>
        </div>
        <button type="button" className="btn btn-danger" onClick={logout}>
          Sign out of this device
        </button>
      </div>

      <ErrorBanner error={error} onDismiss={() => setError(null)} />

      {full && (
        <InfoBanner>
          You are using every device slot. Sign one out below before signing in
          somewhere new.
        </InfoBanner>
      )}

      {capped && (
        <div className="kpi-row" style={{ marginBottom: 'var(--s1)' }}>
          <div className="kpi">
            <div className="kpi-label">Devices in use</div>
            <div className="kpi-value">
              {data.inUse}
              <span style={{ fontSize: 'var(--fs-lg)' }}> / {data.limit}</span>
            </div>
            <div className="kpi-note">
              {full ? 'No slots left' : `${data.limit - data.inUse} still free`}
            </div>
          </div>
        </div>
      )}

      <section className="card">
        <div className="tbl-wrap">
          <table className="tbl">
            <thead>
              <tr>
                <th>Device</th>
                <th>Signed in</th>
                <th>Session expires</th>
                <th aria-label="Actions" />
              </tr>
            </thead>
            <tbody>
              {devices.map((device) => {
                const here = device.deviceFingerprint === thisDevice;
                return (
                  <tr key={device.id}>
                    <td>
                      {device.deviceLabel}
                      {here && <span className="badge" style={{ marginLeft: 8 }}>This device</span>}
                    </td>
                    <td className="mono">{new Date(device.signedInAt).toLocaleString()}</td>
                    <td className="mono">{new Date(device.expiresAt).toLocaleDateString()}</td>
                    <td style={{ textAlign: 'right' }}>
                      <button
                        type="button"
                        className="btn btn-ghost btn-sm"
                        onClick={() => setSigningOut(device)}
                      >
                        <Icon name="logout" size={14} /> Sign out
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {loading && <div className="empty">Loading…</div>}
        {!loading && devices.length === 0 && <div className="empty">No active sessions.</div>}
      </section>

      <p className="tiny muted" style={{ marginTop: 'var(--s2)' }}>
        {user?.role === 'TEACHER' || user?.role === 'STUDENT'
          ? 'Signing a device out ends every session on it. Its next sign-in will need your password again.'
          : 'Your role has no device limit; this list is here so you can end a session you no longer recognise.'}
      </p>

      {signingOut && (
        <Modal
          title="Sign this device out?"
          width={460}
          onClose={() => setSigningOut(null)}
          footer={
            <>
              <button type="button" className="btn btn-secondary" onClick={() => setSigningOut(null)}>
                Cancel
              </button>
              <button type="button" className="btn btn-danger" onClick={() => signOut(signingOut)}>
                Sign out
              </button>
            </>
          }
        >
          <p style={{ margin: 0 }}>
            {signingOut.deviceFingerprint === thisDevice
              ? 'This is the device you are using. Signing it out will return you to the sign-in page.'
              : `${signingOut.deviceLabel} will need to sign in again.`}
          </p>
        </Modal>
      )}

      <Toast message={toast} onDone={() => setTimeout(() => setToast(null), 2400)} />
    </>
  );
}
